import { Alert, cn } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { type PropsWithChildren, type ReactNode, useState } from 'react';
import {
  TITLES_DEFAULT_PARAMS,
  queryOptionsSearchTitlesPaginated,
} from '../../../api/titles/titlesApi';
import {
  isClientLookupFailed,
  isTitlesTooBroad,
} from '../../../api/titles/titlesErrors';
import { narrowsClientSearch } from '../../../api/titles/titlesPlaceholder';
import AddTitleLink from '../../../components/AddTitleLink';
import { AppPagination } from '../../../components/AppPagination';
import { DownloadCsvButton } from '../../../components/DownloadCsvButton';
import { EmbeddedWorkflowView } from '../../../components/EmbeddedWorkflowView';
import { ListToolbar } from '../../../components/ListToolbar';
import { MSR3Empty } from '../../../components/MSR3Empty';
import { PageHeader } from '../../../components/PageHeader';
import { SingleQueryResults } from '../../../components/QueryResults/SingleQueryResults';
import {
  TitleCard,
  TitleCardSkeleton,
} from '../../../components/TitleCard/TitleCard';
import { useActiveWorkspace } from '../../../hooks/useActiveWorkspace';
import { useClientNameSearch } from '../../../hooks/useClientNameSearch';
import { useOwnerClientNames } from '../../../hooks/useClientNames';
import { useEmbeddedWorkflowActive } from '../../../hooks/useEmbeddedWorkflowActive';
import { usePaginationBounds } from '../../../hooks/usePaginationBounds';
import { MSR3BaseLayout } from '../../../layout/MSR3BaseLayout';
import { isCsvExportDisabled } from '../../../utils/csv/csvExport';
import { cappedPagination } from '../../../utils/helpers/cappedTotal';
import { workspaceTypeIsAgent } from '../../../utils/helpers/common';
import { TitlesFilterBar } from './TitlesFilterBar';
import {
  fetchTitlesCsvPage,
  titlesCsvColumns,
  titlesCsvContext,
} from './titlesCsvSpec';
import { useTitleFilters } from './useTitleFilters';

const PAGE_SIZE = TITLES_DEFAULT_PARAMS.pageSize;

const TitlesLayout = ({
  actions,
  children,
}: PropsWithChildren<{ actions?: ReactNode }>) => (
  <MSR3BaseLayout
    classes={{ childrenWrapper: 'space-y-4' }}
    pageHeader={
      <PageHeader
        title="Titles"
        actions={
          <>
            {actions}
            <AddTitleLink />
          </>
        }
      />
    }
  >
    {children}
  </MSR3BaseLayout>
);

/** The loading list: the same six cards a first load shows. */
const TitlesSkeleton = () => (
  <div className="space-y-4">
    {Array.from({ length: 6 }).map((_, index) => (
      <TitleCardSkeleton key={index} />
    ))}
  </div>
);

/**
 * MSR3 Titles list (`/titles`). Card list of real titles with search, five
 * filters behind a toggle, and pagination. Mirrors the Figma list design.
 */
const Content = () => {
  const [activeWorkspace] = useActiveWorkspace();
  const workspaceId = activeWorkspace?._id;
  const isAgent = workspaceTypeIsAgent(activeWorkspace);
  const basePath = `/workspace/${workspaceId ?? ''}`;

  // Tells "owns none of these" apart from "cannot see any": `findAllV2` swallows
  // a failed permission check into an empty 200. Shares the dropdown's cache entry.
  const clientsBaseline = useClientNameSearch({
    enabled: isAgent,
    searchTerm: '',
    workspaceId,
  });

  // Search, filters and page all live in the URL so they survive
  // back-navigation and shared links as one consistent result set.
  const titleFilters = useTitleFilters({
    canSeeClients: clientsBaseline.totalClients > 0,
    // Only agent workspaces render the Client control, and a filter the user
    // cannot see is a filter they cannot undo.
    isClientFilterAvailable: isAgent,
    workspaceId,
  });

  // The search input and the five filter controls are read only by
  // `TitlesFilterBar`, so they go down as one value rather than being unpacked
  // here — this page needs the query arguments and the page.
  const {
    hasFilterSelection,
    hasFilters,
    name,
    page,
    queryFilters,
    searchInput,
    setPage,
  } = titleFilters;

  // Only agent workspaces have clients. The titles service resolves the term
  // to every matching client in the workspace itself, so no id list (and no
  // 100-id cap) passes through the browser.
  const titlesParams = {
    clientName: isAgent && name ? name : undefined,
    name,
    page,
    // Series level only — the backend excludes child episodes.
    seriesOnly: true,
    workspaceId,
    ...queryFilters,
  };

  // A client-name search shows its two refusals inline (below), so it opts out
  // of the interceptor's global error page, which would unmount this page.
  // Any other failure of it falls through to `SingleQueryResults`.
  const titlesQuery = useQuery(
    queryOptionsSearchTitlesPaginated(
      titlesParams,
      titlesParams.clientName ? { suppressErrorModal: true } : undefined,
    ),
  );

  // The too-broad refusal arrives as a result, not an error (see
  // `queryOptionsSearchTitlesPaginated`), so it is cached like rows.
  const result = titlesQuery.data;
  const isTooBroad = isTitlesTooBroad(result);
  const list = isTooBroad ? undefined : result;

  // The lookup failure gets an inline message instead of the error page, but
  // only when the search has no data of its own: a failed refetch of a search
  // that already loaded keeps its list, the rule `SingleQueryResults` applies.
  // A new search holds no previous page (see `keepPreviousForSameSearch`), so
  // `data` is undefined when it fails.
  const searchFailure = isTooBroad
    ? 'too-broad'
    : titlesQuery.isError &&
        !result &&
        titlesParams.clientName &&
        isClientLookupFailed(titlesQuery.error)
      ? 'lookup-failed'
      : undefined;

  const titles = list?.items ?? [];
  // A capped total is a floor, so the pager may go past `meta.totalPages`.
  const { isTotalCapped, itemCount, totalItems, totalPages } = cappedPagination(
    list?.meta,
  );

  // Separate from the filter's ownership lookup on purpose — see `useTitleFilters`.
  const pageClients = useOwnerClientNames(titles, {
    enabled: isAgent,
    workspaceId,
  });

  const { clampedPage } = usePaginationBounds({
    page,
    setPage,
    totalPages,
  });

  // One flag for the input spinner and the list. It is on while the typed term
  // has not reached the URL yet (the debounce window), and while the query
  // fetches with no rows of its own to show. A new term holds no previous
  // rows, so that second case is exactly "fetching for a changed term". A page
  // or sort change keeps its held rows and is not a search.
  const isTermPending = searchInput.trim() !== name.trim();

  // A too-broad alert stays up, with no skeleton, while the user types more
  // onto the refused term: the narrower term is a subset of it, so the alert
  // is still true. The result swaps in when the response says it is not too
  // broad. Any other term (shorter, different) shows the skeleton as usual.
  const isHoldingAlert =
    isTooBroad &&
    isTermPending &&
    narrowsClientSearch({ clientName: searchInput }, { clientName: name });
  const isSearching =
    (isTermPending && !isHoldingAlert) || (titlesQuery.isFetching && !result);

  // The spinner also runs while a held alert waits for its narrower term, so
  // the user can see the search is still going.
  const isSpinning =
    isTermPending ||
    (titlesQuery.isFetching &&
      (!result || (isTooBroad && titlesQuery.isPlaceholderData)));

  // Only the first load locks the search box. After that it stays editable
  // through every fetch, or typing the next character would drop focus.
  const [hasLoaded, setHasLoaded] = useState(false);

  if (!hasLoaded && !titlesQuery.isPending) setHasLoaded(true);

  // The rows on screen belong to the term in the URL, and the totals with
  // them. Neither is shown for a term that is still loading.
  const isResultHidden = isSearching || Boolean(searchFailure);

  // Nothing on this page sets `?src=` any more — Add Title now routes to the
  // interim page, which opens the registration on its own route. The branch
  // stays for hand-typed and shared URLs: when the param does resolve, render
  // the embed in place of the list, mirroring the Home page.
  const isEmbeddingWorkflow = useEmbeddedWorkflowActive();

  // "This workspace has no titles, so there is nothing to search or narrow" —
  // and only a *successful* empty response says that. A failed request also
  // returns no titles, and disabling the controls on it is the one case where
  // they are most needed: a 500 or a dropped connection leaves the page mounted
  // with the error shown, and the user has to be able to clear the term or
  // change the selection that failed rather than hand-edit the URL.
  const isEmptyResult =
    titlesQuery.isSuccess && !isTooBroad && titles.length === 0;
  const isEmptyWithoutFilters = isEmptyResult && !hasFilters;

  // A failed search leaves the search box and filters usable: the term is what
  // the user has to change.
  const areControlsDisabled =
    (titlesQuery.isPending && !hasLoaded) || isEmptyWithoutFilters;

  // A search that finds nothing leaves nothing to narrow, so the filter
  // controls go dead too. Keyed off `hasFilterSelection`, not `hasFilters`: an
  // empty result caused by the filters themselves must stay editable, or the
  // user can't undo the selection that emptied the list.
  const areFiltersDisabled =
    areControlsDisabled || (isEmptyResult && !hasFilterSelection);

  const paginationProps = {
    currentPage: clampedPage,
    // Held rows (a page change in flight) and a search in flight both leave the
    // pager pointing at a result that is about to be replaced.
    disabled:
      areControlsDisabled || titlesQuery.isPlaceholderData || isSearching,
    onPageChange: setPage,
    totalPages,
  };

  if (isEmbeddingWorkflow) {
    return (
      <TitlesLayout>
        <EmbeddedWorkflowView />
      </TitlesLayout>
    );
  }

  // Exports with this render's params, not ones rebuilt from the URL, so an
  // agent search exports with the same `clientName` as the list on screen.
  const csvButton = (
    <DownloadCsvButton
      label="Titles"
      columns={titlesCsvColumns}
      createResolveContext={() => titlesCsvContext(workspaceId ?? '', isAgent)}
      fetchPage={(exportPage, pageSize, signal) =>
        fetchTitlesCsvPage(titlesParams, exportPage, pageSize, signal)
      }
      fileLabel="titles"
      isDisabled={isSearching || isCsvExportDisabled(titlesQuery, totalItems)}
      isRowCountCapped={isTotalCapped}
      rowCount={totalItems}
    />
  );

  return (
    <TitlesLayout actions={csvButton}>
      <TitlesFilterBar
        disabled={areControlsDisabled}
        filtersDisabled={areFiltersDisabled}
        isSearchFetching={isSpinning}
        // Only agent workspaces have clients to filter by.
        showClientFilter={isAgent}
        titleFilters={titleFilters}
        workspaceId={workspaceId}
      />

      {isResultHidden ? (
        // Keeps the toolbar's height while the totals are unknown, so the
        // list does not jump between the alert, the skeleton and the results.
        <div aria-hidden className="invisible" data-testid="list-toolbar-gap">
          <ListToolbar
            currentPage={1}
            disabled
            pageSize={PAGE_SIZE}
            totalItems={0}
            totalPages={1}
            onPageChange={setPage}
          />
        </div>
      ) : (
        <ListToolbar
          {...paginationProps}
          isTotalCapped={isTotalCapped}
          itemCount={itemCount}
          pageSize={PAGE_SIZE}
          totalItems={totalItems}
        />
      )}

      {isSearching ? (
        <TitlesSkeleton />
      ) : (
        <>
          {searchFailure === 'too-broad' && (
            <Alert title="Too many matching clients" variant="warning">
              Too many clients match this name. Type more of the name to see
              titles.
            </Alert>
          )}

          {searchFailure === 'lookup-failed' && (
            <Alert
              title="Client names could not be searched"
              actionButtonProps={{
                children: 'Retry',
                onClick: () => void titlesQuery.refetch(),
              }}
              hasActionButton
              variant="error"
            >
              Client names could not be searched right now. Try again.
            </Alert>
          )}

          {!searchFailure && (
            <SingleQueryResults
              loadingFallback={<TitlesSkeleton />}
              query={titlesQuery}
            >
              {(data) =>
                isTitlesTooBroad(data) ? null : data.items.length === 0 ? (
                  <MSR3Empty
                    {...MSR3Empty.Props[hasFilters ? 'no-result' : 'title']}
                  />
                ) : (
                  // Held rows of the previous page: dimmed until the new one lands.
                  <div
                    className={cn(
                      'space-y-4',
                      titlesQuery.isPlaceholderData && 'opacity-50',
                    )}
                  >
                    {data.items.map((title) => (
                      <TitleCard
                        key={title._id}
                        title={title}
                        basePath={basePath}
                        clientNames={pageClients.getClientNamesOrFallback(
                          title.ownerClientIds,
                        )}
                        isClientNamesLoading={pageClients.isLoading}
                      />
                    ))}
                  </div>
                )
              }
            </SingleQueryResults>
          )}
        </>
      )}

      {!isResultHidden && (
        <div className="flex items-center justify-end">
          <AppPagination {...paginationProps} />
        </div>
      )}
    </TitlesLayout>
  );
};

export const TitlesV3 = () => {
  const [activeWorkspace] = useActiveWorkspace();

  // `/workspace/A/titles` and `/workspace/B/titles` are the same route element,
  // so switching workspace does not remount `Content` on its own. Keying it
  // does: no filter state is meaningful across workspaces, and a remount drops
  // the search and year fields' local input and its pending debounce outright,
  // rather than leaving the resync in `useTitleFilters` to catch up 400ms later
  // on a list that already belongs to B. The header remounts with it, so its
  // CSV button can never export the previous workspace's list.
  return <Content key={activeWorkspace?._id} />;
};
